<template>
  <SDialog v-model:open="dialogVisible">
    <SDialogContent
      class="payment-calculator !flex !flex-col !w-[340px] !max-w-[340px] !top-[22vh] !translate-y-0 !gap-0 !p-0 overflow-hidden"
      :show-close-button="false"
      @escape-key-down.prevent
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle class="sr-only">
          {{ $t("calculator.insert-amount") }}
        </SDialogTitle>
      </SDialogHeader>

      <!-- Amount display -->
      <div class="px-5 pt-5 pb-4">
        <p class="mb-2 text-[13px] font-medium text-muted-foreground">
          {{ $t("calculator.insert-amount") }}
        </p>
        <div
          class="flex h-16 items-center gap-2 rounded-lg border border-input pr-1 pl-4"
        >
          <span class="shrink-0 text-[22px] font-medium text-muted-foreground">
            {{ currentCurrency }}
          </span>
          <input
            class="min-w-0 flex-1 bg-transparent text-right text-[30px] font-semibold tabular-nums outline-none"
            :value="inputContent"
            readonly
            inputmode="none"
            data-cy="payment-calc-amount"
          />
          <SButton
            type="button"
            variant="ghost"
            size="icon"
            class="size-12 shrink-0 text-muted-foreground"
            aria-label="erase"
            @keydown.stop="eraseFromInput"
            @click="eraseFromInput()"
          >
            <!-- size-7 escapes SButton's svg clamp; --s-icon-size is what .s-icon sizes by -->
            <DeleteIcon class="size-7" style="--s-icon-size: 30px" />
          </SButton>
        </div>
      </div>

      <!-- Keypad + Cancel -->
      <div class="flex gap-px border-t border-border bg-border">
        <div class="grid flex-1 grid-cols-3 gap-px bg-border">
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-1"
            @click="updateInput(String(1))"
          >
            1
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-2"
            @click="updateInput(String(2))"
          >
            2
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-3"
            @click="updateInput(String(3))"
          >
            3
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-4"
            @click="updateInput(String(4))"
          >
            4
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-5"
            @click="updateInput(String(5))"
          >
            5
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-6"
            @click="updateInput(String(6))"
          >
            6
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-7"
            @click="updateInput(String(7))"
          >
            7
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-8"
            @click="updateInput(String(8))"
          >
            8
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-9"
            @click="updateInput(String(9))"
          >
            9
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-0"
            @click="updateZero"
          >
            0
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key"
            data-cy="calc-key-00"
            @click="updateInputDoubleZero"
          >
            00
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="calc-key calc-key--clear"
            data-cy="calc-key-clear"
            @click="clearInput"
          >
            {{ $t("calculator.clear") }}
          </SButton>
        </div>

        <SButton
          type="button"
          variant="ghost"
          class="calc-key calc-key--cancel"
          @click="cancel()"
        >
          {{ $t("button.cancel") }}
        </SButton>
      </div>

      <!-- Confirm -->
      <SButton
        type="button"
        class="confirm-button"
        data-cy="confirm-price-btn"
        @click="
          $emit('confirmNumber', Number(inputContent), selectedCashCurrency)
        "
      >
        {{ $t("calculator.confirm-price") }}
      </SButton>
    </SDialogContent>
  </SDialog>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import ModalModule from "@/store/modules/modal";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { DeleteIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: ModalComponents.PaymentCalculator,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    DeleteIcon,
  },
  props: {
    visible: {
      required: false,
      type: Object as PropType<number>,
    },
    placeHolderValue: {
      required: false,
      type: Object as PropType<number>,
    },
  },
  emits: [
    "confirmNumber" /* this event is declared in the parent component Modal*/,
    "cancelCalculator",
  ],
  setup(props) {
    const { t } = useI18n({});
    let dialogVisible = ref(
      props.visible
        ? props.visible
        : dialogVisibleForStatus(ModalStatus.PaymentCalculator)
    );
    let inputContent = props.placeHolderValue
      ? ref(props.placeHolderValue.toString())
      : ref("0.00");
    let initialArray = ["0", "0", "0"];
    let count = 0;

    const orderStore = useOrderStore();
    const { selectedOrder } = storeToRefs(orderStore);

    const orderMetaStore = useOrderMetaStore();
    const { currency } = storeToRefs(orderMetaStore);

    let currentCurrency = computed(() => {
      return currency.value || selectedOrder.value?.orderSession?.currency;
    });

    function clearInput() {
      inputContent.value = "0.00";
      count = 0;
      initialArray = ["0", "0", "0"];
    }

    function updateInputDoubleZero() {
      if (count >= 1) {
        updateInput(String(0));
        updateInput(String(0));
      }
      if (count === 0) {
        return;
      }
    }

    function updateZero() {
      if (count >= 1) {
        updateInput(String(0));
      }
      if (count === 0) {
        return;
      }
    }

    function eraseFromInput() {
      if (count === 1 || count === 0) {
        inputContent.value = "0.00";
        count = 0;
        initialArray = ["0", "0", "0"];
      } else if (count === 2) {
        count = count - 1;
        initialArray.pop();
        initialArray.unshift("0");
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${initialArray[2]}`;
      } else if (count === 3) {
        count = count - 1;
        initialArray.pop();
        initialArray.unshift("0");
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${initialArray[2]}`;
      } else if (count >= 4) {
        count = count - 1;
        initialArray.pop();
        const decimalDivider = initialArray.length - 2;
        inputContent.value =
          initialArray.slice(0, decimalDivider).join("") +
          "." +
          initialArray.slice(decimalDivider, decimalDivider + 2).join("");
      }
    }

    function updateInput(char: string) {
      if (count === 0) {
        inputContent.value = `${initialArray[0]}.${initialArray[1]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 1;
      } else if (count === 1) {
        inputContent.value = `${initialArray[0]}.${initialArray[2]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 2;
      } else if (count === 2) {
        inputContent.value = `${initialArray[1]}.${initialArray[2]}${char}`;
        initialArray.push(char);
        initialArray.shift();
        count = 3;
      } else {
        initialArray.push(char);
        count = count + 1;
        const decimalDivider = initialArray.length - 2;
        inputContent.value =
          initialArray.slice(0, decimalDivider).join("") +
          "." +
          initialArray.slice(decimalDivider, decimalDivider + 2).join("");
      }
    }

    function cancel() {
      // Clear the calculator and close it
      clearInput();
      if (ModalModule.status === ModalStatus.PaymentCalculator) {
        ModalModule.setStatus(ModalStatus.Hidden);
      } else {
        // this is the case where the calculator is not used inside Modal component, lets emit to cancellation event on parent component
        this.$emit("cancelCalculator");
      }
    }

    return {
      dialogVisible,
      inputContent,
      currentCurrency,
      clearInput,
      eraseFromInput,
      updateInput,
      cancel,
      initialArray,
      count,
      t,
      props,
      updateInputDoubleZero,
      updateZero,
      selectedCashCurrency: currency.value,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

// Keypad keys: flush, square, white with hover/pressed states.
// The 1px separators come from the gap-px + bg-border wrappers
.calc-key {
  height: 62px;
  border-radius: 0;
  background-color: #fff;
  color: $color-black-3;
  font-size: 24px;
  font-weight: 400;

  &:hover {
    background-color: #f5f6f7;
  }

  &:active {
    background-color: #eceef0;
  }
}

.calc-key--clear {
  font-size: 16px;
  font-weight: 500;
  color: $color-green2;
}

.calc-key--cancel {
  width: 84px;
  height: auto;
  align-self: stretch;
  font-size: 16px;
  font-weight: 500;
  color: $color-green2;
}

.confirm-button {
  width: 100%;
  height: 60px;
  border-radius: 0;
  background-color: $color-3;
  color: $background-color-clear;
  font-weight: 700;
  font-size: 18px;

  &:hover {
    background-color: $color-3;
    filter: brightness(0.95);
  }
}
</style>
